/**
 * Majhen razčlenjevalnik Markdowna za članke pomoči (brez knjižnice in brez HTML-ja – izris je varen): naslovi
 * (##, ###), odstavki, seznami (- in 1.), **krepko**, `koda`, [povezave](cilj), slike v svoji vrstici ![opis](datoteka)
 * in opombe »> [!NASVET]« / »> [!POZOR]«. Glava članka je med »---« vrsticama (ključ: vrednost).
 */

export type Vrstica =
  | { t: 'besedilo'; v: string }
  | { t: 'krepko'; v: Vrstica[] }
  | { t: 'koda'; v: string }
  | { t: 'povezava'; cilj: string; v: Vrstica[] };

export type Blok =
  | { t: 'naslov'; raven: 2 | 3; v: Vrstica[] }
  | { t: 'odstavek'; v: Vrstica[] }
  | { t: 'seznam'; urejen: boolean; postavke: Vrstica[][] }
  | { t: 'slika'; datoteka: string; opis: string }
  | { t: 'opomba'; vrsta: 'nasvet' | 'pozor'; v: Vrstica[] };

const VZOREC_VRSTICE = /\*\*(.+?)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)/g;

/** Besedilo z oblikovanjem v vrstici. */
export function vrstica(s: string): Vrstica[] {
  const vrni: Vrstica[] = [];
  let zadnji = 0;
  for (const m of s.matchAll(VZOREC_VRSTICE)) {
    if (m.index > zadnji) {
      vrni.push({ t: 'besedilo', v: s.slice(zadnji, m.index) });
    }
    if (m[1] !== undefined) {
      vrni.push({ t: 'krepko', v: vrstica(m[1]) });
    } else if (m[2] !== undefined) {
      vrni.push({ t: 'koda', v: m[2] });
    } else {
      vrni.push({ t: 'povezava', cilj: m[4]!, v: vrstica(m[3]!) });
    }
    zadnji = m.index + m[0].length;
  }
  if (zadnji < s.length) {
    vrni.push({ t: 'besedilo', v: s.slice(zadnji) });
  }
  return vrni;
}

const SEZNAM = /^\s*-\s+(.*)$/;
const UREJEN = /^\s*\d+\.\s+(.*)$/;
const SLIKA = /^!\[([^\]]*)\]\(([^)\s]+)\)\s*$/;
const OPOMBA = /^\[!(NASVET|POZOR)\]\s*(.*)$/i;

/** Bloki iz Markdowna (brez glave). */
export function bloki(md: string): Blok[] {
  const vrni: Blok[] = [];
  const vrstice = md.replace(/\r\n/g, '\n').split('\n');
  let i = 0;
  const prazna = (s: string | undefined) => s === undefined || s.trim() === '';
  while (i < vrstice.length) {
    const v = vrstice[i]!;
    if (prazna(v)) {
      i++;
      continue;
    }
    const naslov = /^(#{2,3})\s+(.*)$/.exec(v);
    if (naslov) {
      vrni.push({ t: 'naslov', raven: naslov[1]!.length as 2 | 3, v: vrstica(naslov[2]!.trim()) });
      i++;
      continue;
    }
    const slika = SLIKA.exec(v.trim());
    if (slika) {
      vrni.push({ t: 'slika', opis: slika[1]!, datoteka: slika[2]! });
      i++;
      continue;
    }
    if (v.startsWith('>')) {
      const deli: string[] = [];
      while (i < vrstice.length && vrstice[i]!.startsWith('>')) {
        deli.push(vrstice[i]!.replace(/^>\s?/, ''));
        i++;
      }
      let besedilo = deli.join(' ').trim();
      let vrsta: 'nasvet' | 'pozor' = 'nasvet';
      const o = OPOMBA.exec(besedilo);
      if (o) {
        vrsta = o[1]!.toUpperCase() === 'POZOR' ? 'pozor' : 'nasvet';
        besedilo = o[2]!.trim();
      }
      vrni.push({ t: 'opomba', vrsta, v: vrstica(besedilo) });
      continue;
    }
    if (SEZNAM.test(v) || UREJEN.test(v)) {
      const urejen = UREJEN.test(v);
      const vzorec = urejen ? UREJEN : SEZNAM;
      const postavke: string[] = [];
      while (i < vrstice.length && !prazna(vrstice[i])) {
        const m = vzorec.exec(vrstice[i]!);
        if (m) {
          postavke.push(m[1]!.trim());
        } else if (/^\s/.test(vrstice[i]!)) {
          // nadaljevanje postavke v zamaknjeni vrstici
          postavke[postavke.length - 1] += ' ' + vrstice[i]!.trim();
        } else {
          break;
        }
        i++;
      }
      vrni.push({ t: 'seznam', urejen, postavke: postavke.map(vrstica) });
      continue;
    }
    const odstavek: string[] = [];
    while (
      i < vrstice.length &&
      !prazna(vrstice[i]) &&
      !/^#{2,3}\s/.test(vrstice[i]!) &&
      !vrstice[i]!.startsWith('>') &&
      !SEZNAM.test(vrstice[i]!) &&
      !UREJEN.test(vrstice[i]!) &&
      !SLIKA.test(vrstice[i]!.trim())
    ) {
      odstavek.push(vrstice[i]!.trim());
      i++;
    }
    vrni.push({ t: 'odstavek', v: vrstica(odstavek.join(' ')) });
  }
  return vrni;
}

/** Glava (»---« … »---«) kot ključ → vrednost in preostanek besedila. */
export function glava(md: string): { glava: Record<string, string>; telo: string } {
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(md);
  if (!m) {
    return { glava: {}, telo: md };
  }
  const g: Record<string, string> = {};
  for (const v of m[1]!.split(/\r?\n/)) {
    const i = v.indexOf(':');
    if (i > 0) {
      g[v.slice(0, i).trim()] = v.slice(i + 1).trim();
    }
  }
  return { glava: g, telo: md.slice(m[0].length) };
}

/** Golo besedilo vrstice (za iskanje). */
export function golo(v: Vrstica[]): string {
  return v.map((x) => (x.t === 'besedilo' || x.t === 'koda' ? x.v : golo(x.v))).join('');
}
